import { observable, action } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";

/**
 * Client-side state for the copilot onboarding flows (new-user 10-step setup
 * and existing-user account digest). Owns the step machine, the artifacts
 * collected along the way, resume, and the completed / dismissed gates.
 *
 * The record lives on the server (cm_member_attribute, via
 * /cm/member/copilotOnboarding), not in the browser: "done" and "dismissed"
 * hold across devices and browsers, and the server can see where a member is.
 * It arrives with the boot-time /cm/member/onboardingData payload
 * (hydrateFromServer) and every change is written back, debounced.
 */

const PERSIST_DEBOUNCE_MS = 300;

const EMPTY_ARTIFACTS = {
  website: "",
  draftProfile: null,
  savedProfileId: "",
  icpFilters: null,
  searchTotal: 0,
  listId: "",
  listName: "",
  enrichChoice: "",
  emailConnected: false,
  linkedInConnected: false,
  sequenceId: ""
};

class OnboardingCopilotStore {
  @observable mode = null; // "onboarding" | "digest" | null
  @observable step = 0;
  @observable artifacts = { ...EMPTY_ARTIFACTS };
  @observable dismissCount = 0;
  status = "idle"; // "idle" | "active" | "done" | "dismissed"
  memberId = "";
  // Existing-user digest: shown once per member, tracked on the same record.
  digestShownAt = null;
  // Server record. Unknown until the boot payload (or a fetch) arrives; the
  // gate treats unknown as "do not offer" so a slow load never re-nags.
  _hydrated = false;
  _persistTimer = null;

  @action begin = (mode, memberId) => {
    this.mode = mode;
    this.memberId = memberId;
    // Only a never-started run gets cleared. Keying on "not active" meant
    // re-entering a completed or dismissed run wiped the record of what it
    // built (saved profile, list, sequence). "Start over" sets idle first, so
    // that path still resets.
    if (this.status === "idle") {
      this.step = 0;
      this.artifacts = { ...EMPTY_ARTIFACTS };
    }
    this.status = "active";
    this.persist();
  };

  @action setStep = n => {
    this.step = n;
    this.persist();
  };

  @action patchArtifacts = obj => {
    this.artifacts = { ...this.artifacts, ...obj };
    this.persist();
  };

  @action dismiss = () => {
    this.dismissCount += 1;
    // One and done: "I'll explore on my own" means it. Re-offering on the next
    // visit read as nagging.
    this.status = "dismissed";
    this.mode = null;
    this.persist();
  };

  @action complete = () => {
    this.status = "done";
    this.mode = null;
    this.persist();
  };

  // "fresh" -> never started, offer from step 0.
  // "resume" -> was active past step 0, offer to pick up.
  // null -> completed, dismissed, or not yet known: never offer.
  shouldOffer = memberId => {
    this.hydrate(memberId);
    if (!this._hydrated) return null;
    if (this.status === "done" || this.status === "dismissed") return null;
    if (this.status === "active" && this.step > 0) return "resume";
    return "fresh";
  };

  snapshot = () => ({
    status: this.status,
    step: this.step,
    artifacts: this.artifacts,
    dismissCount: this.dismissCount,
    digestShown: !!this.digestShownAt
  });

  @action markDigestShown = () => {
    if (!this.digestShownAt) this.digestShownAt = new Date().toISOString();
    this.persist();
  };

  persist = () => {
    if (this._persistTimer) clearTimeout(this._persistTimer);
    this._persistTimer = setTimeout(() => {
      this._persistTimer = null;
      try {
        Promise.resolve(makeApi({ url: URLS.copilotOnboardingState, method: "POST", data: this.snapshot() })).catch(() => {
          /* fire-and-forget: the next change writes again */
        });
      } catch (e) {
        /* same: never let a persist failure break the flow */
      }
    }, PERSIST_DEBOUNCE_MS);
  };

  // Applies the server record (boot payload or a fetch).
  @action hydrateFromServer = data => {
    if (!data || typeof data !== "object") return;
    this.status = data.status || "idle";
    this.step = Number(data.step) || 0;
    this.artifacts = { ...EMPTY_ARTIFACTS, ...(data.artifacts || {}) };
    this.dismissCount = Number(data.dismissCount) || 0;
    this.digestShownAt = data.digestShownAt || null;
    this._hydrated = true;
  };

  // One-time fetch for a session that opened the copilot before the boot
  // payload landed (or never loaded it).
  fetchFromServer = async () => {
    try {
      const res = await makeApi({ url: URLS.copilotOnboardingState, method: "GET" });
      if (res && res.data && typeof res.data.status === "string") this.hydrateFromServer(res.data);
    } catch (e) {
      /* stays unhydrated: the gate will not offer */
    }
    return this.snapshot();
  };

  @action hydrate = memberId => {
    this.memberId = memberId;
    if (!this._hydrated) this.fetchFromServer();
  };

  // Tests share this singleton; without an explicit reset, artifacts from one
  // case leak into the next and a state-driven flow resumes mid-way. A reset
  // is a known state, so it counts as hydrated.
  @action reset = () => {
    this.mode = null;
    this.step = 0;
    this.status = "idle";
    this.artifacts = { ...EMPTY_ARTIFACTS };
    this.dismissCount = 0;
    this.digestShownAt = null;
    this.memberId = "";
    this._hydrated = true;
    if (this._persistTimer) {
      clearTimeout(this._persistTimer);
      this._persistTimer = null;
    }
  };
}

const onboardingCopilotStore = new OnboardingCopilotStore();
export default onboardingCopilotStore;
export { OnboardingCopilotStore, EMPTY_ARTIFACTS };
